
/**
 * Builds the session.update payload sent once the data channel opens.
 * Split out of useRealtimeVoice.js 2026-08-23 to keep that file under the
 * repo's 300-line cap - pure data construction, no behavior change.
 *
 * FIXED 2026-08-09 (real, confirmed live error, seen on screen: "Missing
 * required parameter: 'session.type'"): the current Realtime API requires
 * `type: "realtime"` INSIDE the session object on every session.update
 * event, same as the mint-time session_config already has server-side.
 * Without it, OpenAI rejected every update - which is where tools/
 * turn_detection/temperature get applied, so the live call had correct
 * instructions (from mint) but NONE of those, silently, until the
 * resulting error surfaced in the UI.
 *
 * voice, input_audio_transcription, and turn_detection all live under
 * nested audio.output/audio.input, NOT flat top-level session fields, in
 * the current Realtime API - confirmed via a real end-to-end connection
 * test (aiortc, full ICE/DTLS handshake, not just SDP exchange) that
 * returned "Unknown parameter" errors one at a time for each flat field
 * until corrected. session.type is also required (separately confirmed
 * live, on-screen, as "Missing required parameter: 'session.type'").
 *
 * REMOVED 2026-08-22: a top-level `include` field here was rejected live
 * ("Unknown parameter: 'include'"), likely voiding the whole
 * session.update - the same test's missing user transcripts. Removed,
 * not relocated by guess; transcriptionConfidence() safely no-ops with no
 * logprobs, so the trust boundary still runs on the audio-overlap signal
 * alone.
 *
 * temperature is NOT a valid session.update field in the current API
 * ("Unknown parameter: 'session.temperature'", confirmed live) - dropped
 * rather than guessing at a new location, since it's not audio-related
 * like the other three fields that just moved.
 */

// The session's input-audio transcription model; usage receipts name the same one (realtimeDiagnostics.js).
export const TRANSCRIPTION_MODEL = "gpt-4o-transcribe";
// Conversation between a person and a home voice assistant named Aria. English or Spanish.
// 2026-10-22: no list of goodbye words. The hint went live in Room 214 on 2026-10-21 (first hinted transcription
// 04:54 CDT); every disputed hang-up since came back as a word from that list ("Goodbye" rt_r7yjpasv, "That's all"
// rt_qppzkpzp, "go away" rt_4ze7jvrt) from 0.1-0.2 s of detected speech. Hint bias is a hypothesis, not proven.
// The language guidance stays.
export const TRANSCRIPTION_HINT = "A person talking with their home voice assistant, Aria. Mostly English, sometimes Spanish.";
// Owner 2026-10-22 "lock english": with recognition pinned to English the hint does not mention Spanish.
export const TRANSCRIPTION_HINT_ENGLISH = "A person talking with their home voice assistant, Aria. English.";
const transcriptionHint = () =>
  process.env.REACT_APP_TRANSCRIPTION_LANGUAGE === "en" ? TRANSCRIPTION_HINT_ENGLISH : TRANSCRIPTION_HINT;

export function buildSessionUpdate({ caos, voice }) {
  const update = {
    type: "session.update",
    session: {
      type: "realtime",
      instructions: caos.instructions,
      audio: {
        output: { voice: caos.voice || voice },
        input: {
          // 2026-08-22: whisper-1 -> gpt-4o-transcribe (confirmed valid in
          // this exact field for a normal realtime conversational session).
          // Real Room 102 evidence showed correctly-bounded turns with
          // wrong recognized words (VAD wasn't the problem); gpt-4o-transcribe
          // is documented to improve recognition accuracy over whisper-1.
          //
          // 2026-09-10 (Terminal 10, verified against current OpenAI docs):
          // a full conversational session (session.type: "realtime", ours)
          // only accepts gpt-4o-transcribe / gpt-4o-mini-transcribe / whisper-1
          // as input_audio_transcription.model - these use the singular,
          // OPTIONAL `language` hint. The newer multi-language `languages`
          // array (built for code-switching) belongs to gpt-transcribe /
          // gpt-live-transcribe, which are only valid inside a DEDICATED
          // session.type:"transcription" session, not this one - confirmed,
          // not guessed, so we do not swap the model. `language` was hard-
          // coded to "en", forcing every utterance through English
          // recognition even when the resident speaks Spanish or switches
          // mid-sentence (Michael's "dos savor" case). Omitting the hint
          // lets the model auto-detect the spoken language per turn instead.
          // RQ-055 (owner decision, OFF by default): build the page with
          // REACT_APP_TRANSCRIPTION_LANGUAGE=en to pin recognition to English.
          // Evidence: Room 214 rt_t07bct8g, short English commands such as
          // "end the call" were written as Lithuanian/Arabic/Korean/Hindi text.
          transcription: { model: TRANSCRIPTION_MODEL, ...(process.env.REACT_APP_TRANSCRIPTION_LANGUAGE ? { language: process.env.REACT_APP_TRANSCRIPTION_LANGUAGE } : {}),
            // RQ-056 (OFF by default; build with REACT_APP_TRANSCRIPTION_HINT=1): `prompt` is a documented optional
            // field (max 1024 chars) that biases vocabulary WITHOUT pinning the language, so Spanish still works.
            ...(process.env.REACT_APP_TRANSCRIPTION_HINT === "1" ? { prompt: transcriptionHint() } : {}) },
          ...(caos.noise_reduction ? { noise_reduction: caos.noise_reduction } : {}),
          ...(caos.turn_detection ? { turn_detection: { ...caos.turn_detection, create_response: false } } : {}),
        },
      },
    },
  };
  if (caos.tools) update.session.tools = caos.tools;
  if (caos.tool_choice) update.session.tool_choice = caos.tool_choice;
  return update;
}
